/* Lead-kaart in de Kanban — bron-kleur, enrichment/ScalIQ-chips, hover-preview. */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { openChat } from "@/lib/chatBus";
import { toast } from "sonner";
import i18n from "i18next";
import { useNavigate } from "react-router-dom";
import { Loader2, Network, Send, Mail, MessageCircle, CheckCircle2, Sparkles, Rocket, TrendingUp, User as UserIcon } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { STAGES, SOURCE_STYLE, sourceOf, fmtEur } from "./constants";
import { probColor } from "@/components/sales/ForecastPanel";

/* ------------------------------------------------------------------ */
/*  Lead card (colored by source)                                      */
/* ------------------------------------------------------------------ */
function LeadCard({ lead: l, stageId, prob, onMove, onOpen, selected, onToggleSelect, onDragStart, onDragEnd, isDragging }) {
    const src = sourceOf(l);
    const style = SOURCE_STYLE[src] || SOURCE_STYLE.agent;
    const stCount = (l.stakeholders || []).length;
    const Icon = style.Icon;
    const navigate = useNavigate();
    const [drafting, setDrafting] = useState(false);
    // Full title-attribute string so browser gives an instant native tooltip
    // when the pipeline column is narrow and text is truncated.
    const nativeTip = [
        l.company_name,
        l.contact_name && `· ${l.contact_name}`,
        l.contact_title && `(${l.contact_title})`,
    ].filter(Boolean).join(" ");

    const draftOutreach = async (ev) => {
        ev.stopPropagation();
        setDrafting(true);
        try {
            const r = await api.post("/outreach/bulk-generate", {
                lead_ids: [l.id], submit_for_approval: false,
            });
            const first = (r.data?.results || [])[0];
            if (first?.error) throw new Error(first.error);
            toast.success(i18n.t("pipeline_sales.draft_ready", { company: l.company_name }));
            navigate("/app/sales/outreach");
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || i18n.t("pipeline_sales.draft_failed"));
        } finally {
            setDrafting(false);
        }
    };
    const askBriefing = (ev) => {
        ev.stopPropagation();
        const prompt = i18n.t("pipeline_sales.briefing_prompt", { company: l.company_name, contact: l.contact_name ? ` (${l.contact_name})` : "" });
        openChat({ masterId: "master_sales_agent", context: `lead:${l.id}`, prompt });
    };

    return (
        <HoverCard openDelay={220} closeDelay={80}>
            <HoverCardTrigger asChild>
        <div data-testid={`sales-card-${l.id}`}
            draggable={!l.is_archived}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            title={nativeTip}
            className={`group relative w-full text-left rounded-lg p-3 border-2 transition-all hover:translate-y-[-1px] hover:shadow-[0_6px_20px_-6px_rgba(0,0,0,0.35)] cursor-grab active:cursor-grabbing
                ${style.cardBorder}
                ${selected ? "shadow-[0_0_0_2px_var(--jr-brand)]" : ""}
                ${l.is_archived ? "opacity-60 border-dashed cursor-not-allowed" : ""}
                ${isDragging ? "opacity-40 rotate-1 scale-95" : ""}`}
            onClick={onOpen}>
            {/* Source accent stripe */}
            <span aria-hidden className={`absolute left-0 top-2 bottom-2 w-1 rounded-r ${style.leftAccent} relative before:absolute before:inset-0 before:rounded-r`} />
            <div className="flex items-start justify-between gap-2">
                <button onClick={onToggleSelect} data-testid={`select-${l.id}`}
                    aria-label="Selecteer lead"
                    className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${selected ? "bg-[var(--jr-brand)] border-[var(--jr-brand)]" : "border-white/25 hover:border-white/40"}`}>
                    {selected && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#000" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </button>
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-white truncate" title={l.company_name}>{l.company_name}</div>
                    <div className="text-xs text-[var(--jr-text-2)] truncate" title={[l.contact_name, l.contact_title].filter(Boolean).join(" · ")}>
                        {l.contact_name || "—"}
                        {l.contact_title && <span className="text-[var(--jr-text-3)]"> · {l.contact_title}</span>}
                    </div>
                </div>
                <span data-testid={`source-badge-${l.id}`}
                    title={style.label}
                    className={`text-[9px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded ${style.badgeBg} ${style.badgeText} border ${style.badgeBorder} shrink-0 inline-flex items-center gap-1`}>
                    <Icon size={8} /> {style.label}
                </span>
            </div>
            {stCount > 0 && (
                <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-[var(--jr-info)]">
                    <Network size={10} /> {stCount} stakeholder{stCount === 1 ? "" : "s"}
                </div>
            )}
            {l.enrichment_status === "enriching" && (
                <div data-testid={`enrich-${l.id}`}
                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-info)]">
                    <Loader2 size={10} className="animate-spin" /> Surfe enriching…
                </div>
            )}
            {l.enrichment_status === "enriched" && (
                <div data-testid={`enrich-done-${l.id}`}
                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.16em] text-[#22c55e]">
                    <CheckCircle2 size={10} /> Surfe-data ✓
                </div>
            )}
            {l.enrichment_status === "failed" && (
                <div data-testid={`enrich-fail-${l.id}`} title={i18n.t("pipeline_sales.surfe_none_tip")}
                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">
                    × {i18n.t("pipeline_sales.surfe_no_match")}
                </div>
            )}
            {l.scaliq && (
                <div data-testid={`scaliq-chip-${l.id}`}
                    title={`ScalIQ: ${l.scaliq.campaign_name || l.scaliq.campaign_id}`}
                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.16em] text-[#818cf8]">
                    <Rocket size={10} /> ScalIQ
                    {l.scaliq.replied ? ` · ${i18n.t("scaliq.st_replied")}`
                        : l.scaliq.connection_status === "accepted" ? ` · ${i18n.t("scaliq.st_accepted")}` : ""}
                </div>
            )}
            <div className="mt-2 flex flex-wrap gap-1 items-center">
                {/* Stage-pills alleen zichtbaar bij hover — voorkomt misklikken
                    wanneer je de kaart wil openen (drawer). */}
                {STAGES.filter((s) => s.id !== stageId).slice(0, 3).map((s) => (
                    <span key={s.id} onClick={(e) => onMove(l.id, s.id, e)} data-testid={`move-${l.id}-${s.id}`}
                        title={i18n.t("pipeline_sales.move_to", { stage: s.label })}
                        className="opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5 cursor-pointer">
                        →{s.label.slice(0, 3)}
                    </span>
                ))}
                {/* Dealwaarde in EUR */}
                {fmtEur(l.deal_value) && (
                    <span data-testid={`value-chip-${l.id}`}
                        title={i18n.t("pipeline_sales.deal_value")}
                        className="ml-auto inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded-full border border-[var(--jr-amber)]/40 text-[var(--jr-amber)]">
                        {fmtEur(l.deal_value)}
                    </span>
                )}
                {/* Sprint 6 · #21 — win-kans chip (AI-voorspelling) */}
                {prob && (
                    <span data-testid={`prob-chip-${l.id}`}
                        title={`${i18n.t("forecast.title")}: ${Math.round(prob.probability * 100)}%${
                            prob.factors?.filter((f) => !f.startsWith("stage_")).length
                                ? ` · ${prob.factors.filter((f) => !f.startsWith("stage_")).map((f) => i18n.t(`forecast.factor_${f}`)).join(", ")}`
                                : ""}`}
                        className={`inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded-full border ${fmtEur(l.deal_value) ? "" : "ml-auto"}`}
                        style={{ color: probColor(prob.probability), borderColor: `${probColor(prob.probability)}55` }}>
                        <TrendingUp size={8} /> {Math.round(prob.probability * 100)}%
                    </span>
                )}
            </div>
            {l.created_by_name && src !== "agent" && (
                <div className="mt-1.5 pt-1.5 border-t border-white/5 text-[10px] text-[var(--jr-text-3)]">
                    {i18n.t("pipeline_sales.by_user", { name: l.created_by_name })}
                </div>
            )}
        </div>
            </HoverCardTrigger>
            <HoverCardContent side="right" align="start" sideOffset={10}
                className="w-80 z-50 rounded-xl border border-white/10 bg-[var(--jr-surface)] p-4 text-white shadow-2xl"
                data-testid={`lead-hover-${l.id}`}>
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-sm font-semibold leading-snug break-words">{l.company_name || i18n.t("pipeline_sales.unknown_company")}</div>
                        {l.company_website && <div className="text-[11px] text-[var(--jr-text-3)] truncate mt-0.5">{l.company_website}</div>}
                    </div>
                    <span className={`text-[9px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded ${style.badgeBg} ${style.badgeText} border ${style.badgeBorder} shrink-0 inline-flex items-center gap-1`}>
                        <Icon size={8} /> {style.label}
                    </span>
                </div>
                {l.contact_name && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-[var(--jr-text-2)]">
                        <UserIcon size={12} className="text-[var(--jr-text-3)]"/>
                        <span className="text-white">{l.contact_name}</span>
                        {l.contact_title && <span className="text-[var(--jr-text-3)]">· {l.contact_title}</span>}
                    </div>
                )}
                {(l.contact_email || l.contact_phone) && (
                    <div className="mt-1.5 space-y-0.5 text-xs">
                        {l.contact_email && <div className="text-[var(--jr-text-2)] truncate"><Mail size={10} className="inline mr-1.5 text-[var(--jr-text-3)]"/>{l.contact_email}</div>}
                        {l.contact_phone && <div className="text-[var(--jr-text-2)]"><MessageCircle size={10} className="inline mr-1.5 text-[var(--jr-text-3)]"/>{l.contact_phone}</div>}
                    </div>
                )}
                {l.signal_summary && (
                    <div className="mt-3 rounded-md bg-white/[0.04] border border-white/6 p-2 text-[11px] text-[var(--jr-text-2)] leading-relaxed line-clamp-3">
                        <Sparkles size={10} className="inline mr-1 text-[var(--jr-brand)]"/> {l.signal_summary}
                    </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)]">
                    {prob && (
                        <span className="inline-flex items-center gap-1" style={{ color: probColor(prob.probability) }} data-testid={`hover-prob-${l.id}`}>
                            <TrendingUp size={9}/> {Math.round(prob.probability * 100)}% {i18n.t("forecast.win_chance")}
                        </span>
                    )}
                    {stCount > 0 && <span className="inline-flex items-center gap-1 text-[var(--jr-info)]"><Network size={9}/> {stCount} stakeholders</span>}
                    {l.enrichment_status === "enriched" && <span className="inline-flex items-center gap-1 text-[#22c55e]"><CheckCircle2 size={9}/> Surfe ✓</span>}
                    {l.contact_language && <span>· {l.contact_language.toUpperCase()}</span>}
                    {l.created_by_name && <span>· {l.created_by_name}</span>}
                </div>
                {/* Quick-actions row */}
                <div className="mt-3 pt-3 border-t border-white/6 flex items-center gap-2">
                    <button onClick={draftOutreach} disabled={drafting}
                        data-testid={`hover-draft-${l.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[var(--jr-brand)]/12 border border-[var(--jr-brand)]/30 text-[11px] font-medium text-[var(--jr-brand)] hover:bg-[var(--jr-brand)]/20 disabled:opacity-60 transition-colors">
                        {drafting ? <Loader2 className="animate-spin" size={11}/> : <Send size={11}/>}
                        Draft outreach
                    </button>
                    <button onClick={askBriefing}
                        data-testid={`hover-briefing-${l.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white/[0.04] border border-white/10 text-[11px] font-medium text-[var(--jr-text-2)] hover:text-white hover:bg-white/[0.08] transition-colors">
                        <Sparkles size={11}/>
                        AI briefing
                    </button>
                </div>
                <div className="mt-2 text-[9px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">
                    Klik card → open detail · sleep → verplaats
                </div>
            </HoverCardContent>
        </HoverCard>
    );
}

export default LeadCard;
